/**
 * The single definition of every market the site serves. Everything that
 * needs a locale's domain, path prefix, ISO code, content language, fallback
 * or redirect file should derive it from here.
 *
 * Keys are lowercase Kontent language codenames ('default' is the US site).
 * `fallback` mirrors the Kontent language fallback chain (checked against
 * the Kontent project on 2026-10-26): untranslated content in a market is
 * served from its fallback language.
 */
const US_ORIGIN = 'https://www.sunlighten.com';

const markets = {
    default: {
        codename: 'default',
        iso: 'en-US',
        origin: US_ORIGIN,
        pathPrefix: '',
        fallback: null,
        redirectsFile: 'urlRedirects.json',
    },
    'gb-en': { codename: 'GB-en', iso: 'en-GB', fallback: 'default' },
    'my-en': { codename: 'MY-en', iso: 'en-MY', fallback: 'gb-en' },
    'vn-en': { codename: 'VN-en', iso: 'en-VN', fallback: 'gb-en' },
    'de-de': { codename: 'DE-de', iso: 'de-DE', fallback: 'gb-en' },
    'fr-fr': { codename: 'FR-fr', iso: 'fr-FR', fallback: 'gb-en' },
    'sg-en': { codename: 'SG-en', iso: 'en-SG', fallback: 'gb-en' },
    'au-en': {
        codename: 'AU-en',
        iso: 'en-AU',
        // Served from its own domain root, without a /au-en prefix.
        origin: 'https://www.sunlighten.com.au',
        pathPrefix: '',
        fallback: 'gb-en',
        redirectsFile: 'urlRedirects-au.json',
    },
    'nz-en': {
        codename: 'NZ-en',
        iso: 'en-NZ',
        // NZ hosting redirects www to the apex host.
        origin: 'https://sunlighten.co.nz',
        pathPrefix: '',
        fallback: 'au-en',
        redirectsFile: 'urlRedirects-nz.json',
    },
};

// Markets without their own domain live under the US site at /{key}.
for (const [key, market] of Object.entries(markets)) {
    market.key = key;
    market.language = market.iso.split('-')[0];
    market.origin = market.origin || US_ORIGIN;
    market.pathPrefix = market.pathPrefix ?? `/${key}`;
    market.redirectsFile =
        market.redirectsFile || markets.default.redirectsFile;
}

const normalizeMarketKey = (locale) => (locale || 'default').toLowerCase();

/** The market for a Kontent codename (any case), or undefined if unknown. */
function getMarket(locale) {
    return markets[normalizeMarketKey(locale)];
}

/** The market whose site is served from `origin`, if it has its own domain. */
function getMarketForOrigin(origin) {
    return Object.values(markets).find(
        (market) => market.pathPrefix === '' && market.origin === origin
    );
}

module.exports = {
    markets,
    getMarket,
    getMarketForOrigin,
    normalizeMarketKey,
};
